/**
 * Import Wizard State Machine Types
 *
 * This file contains all TypeScript types, enums, and helper functions
 * for the XState-based wizard state machine.
 *
 * Architecture: Unified OPERATING flow
 * All async operations go through a single OPERATING state with phases:
 *   preparing → saving → [executing] → [polling] → reloading → routing
 */

import { ImportData, InternalColumns } from '../importer-types'

/**
 * Machine ID constant - used for absolute state references
 */
export const MACHINE_ID = 'importWizard'

/**
 * Wizard steps - the main navigation states (user-facing)
 */
export enum WizardStep {
  STEP1_CONFIRM = 'step1',
  STEP2_REVIEW = 'step2',
  STEP3_DECIDE = 'step3',
  STEP4_IMPORT = 'step4',
}

/**
 * Step order for navigation calculations
 */
const STEP_ORDER: WizardStep[] = [
  WizardStep.STEP1_CONFIRM,
  WizardStep.STEP2_REVIEW,
  WizardStep.STEP3_DECIDE,
  WizardStep.STEP4_IMPORT,
]

/**
 * Internal machine states - non-step states for async operations
 */
export enum InternalState {
  INITIALIZING = 'initializing',
  OPERATING = 'operating',
  ERROR = 'error',
  /** Waiting for existing backend process to complete (on initial load) */
  WAITING_FOR_PROCESS = 'waitingForProcess',
}

/**
 * Phases within the OPERATING state
 */
export enum OperatingPhase {
  PREPARING = 'preparing',
  SAVING = 'saving',
  EXECUTING = 'executing',
  POLLING = 'polling',
  RELOADING = 'reloading',
  ROUTING = 'routing',
}

/**
 * Operation types - what kind of operation is being performed
 */
export enum OperationType {
  /** Navigation only - save data and route to next/prev/same step */
  NAVIGATE = 'navigate',
  /** Set companies - calls set-companies API */
  SET_COMPANIES = 'set_companies',
  /** Set tags - calls set-tags API */
  SET_TAGS = 'set_tags',
  /** Set deals - calls set-deals API */
  SET_DEALS = 'set_deals',
  /** Set contacts - calls set-contacts API */
  SET_CONTACTS = 'set_contacts',
  /** Set notes - calls set-company-notes API */
  SET_NOTES = 'set_notes',
}

/**
 * Navigation direction for NAVIGATE operations
 */
export type NavigationDirection = 'forward' | 'back' | 'stay'

/**
 * Operation being performed - stored in context during OPERATING state
 */
export interface Operation {
  /** Type of operation */
  type: OperationType
  /** Navigation direction (only for NAVIGATE operations) */
  direction: NavigationDirection
  /** Step where the operation originated */
  originStep: WizardStep
  /** Prepared data to save (may have transforms applied) */
  preparedData: ImportData | null
}

/**
 * Nested states within step states
 */
export enum NestedState {
  // Common viewing state
  VIEWING = 'viewing',

  // Step 1 nested states
  DESELECT_MODAL = 'deselectModal',

  // Step 3 nested states
  CONFIRM_MODAL = 'confirmModal',

  // Step 4 nested states (deal validation flow)
  VALIDATING_DEALS = 'validatingDeals',
  DEAL_PRODUCT_MODAL = 'dealProductModal',
  DEAL_VALIDATION_MODAL = 'dealValidationModal',
}

/**
 * All event types the machine can receive
 */
export enum EventType {
  // Unified operation event
  START_OPERATION = 'START_OPERATION',

  // Step-aware "Next" button — machine handles per-step behavior
  NEXT = 'NEXT',

  // Step 1: Deselect confirmation
  CONFIRM_DESELECT = 'CONFIRM_DESELECT',
  CANCEL_DESELECT = 'CANCEL_DESELECT',

  // Step 3: Confirm matches
  CONFIRM_MATCHES = 'CONFIRM_MATCHES',
  CANCEL_CONFIRM = 'CANCEL_CONFIRM',

  // Step 4 deal validation
  VALIDATE_DEALS = 'VALIDATE_DEALS',
  CANCEL_DEAL_VALIDATION = 'CANCEL_DEAL_VALIDATION',
  DEAL_PRODUCT_SELECTED = 'DEAL_PRODUCT_SELECTED',
  DEALS_VALIDATION_FIXED = 'DEALS_VALIDATION_FIXED',

  // Error recovery
  RETRY = 'RETRY',
}

/**
 * Machine input - provided when creating the machine
 */
export interface WizardMachineInput {
  importNumber: number
}

/**
 * Context stored in the state machine
 *
 * Note: Company categorization (companiesPrematched, companiesPendingReview, etc.)
 * is computed in ImporterViewModel, not stored in machine context.
 * The machine only stores what it needs for its own logic (routing, operations).
 */
export interface WizardContext {
  /** Import number from URL */
  importNumber: number

  /** Import name (filename) */
  importName: string

  /** Import data from API */
  importData: ImportData | null

  /** Skip matching mode */
  skipMatching: boolean

  /** Steps that are currently available for navigation */
  availableSteps: WizardStep[]

  /** Increments on each data refresh - used to reset local UI state */
  dataVersion: number

  /** Current operation being performed (null when idle) */
  operation: Operation | null

  /** Target step to route to after OPERATING completes */
  targetStep: WizardStep | null

  /** Error message (if any) */
  error: string | null

  /** Current validation error data (when deal validation fails) */
  dealValidationData:
    | {
        type: 'mapping'
        missing: string[]
        found: string[]
        title: string
        itemName: string
        columnName: 'deal_product' | 'deal_account_owner' | 'deal_sales_stage'
      }
    | {
        type: 'product_required'
        title: string
        message: string
      }
    | null

  /** Whether user initiated deal import and validation gate should auto-trigger */
  dealValidationPending: boolean

  /** Company hashes selected for deselection in Step 1 (stored between NEXT and CONFIRM/CANCEL) */
  deselectedHashes: string[]
}

/**
 * All events the machine can receive
 */
export type WizardEvent =
  // Unified operation event (for back/save)
  | {
      type: EventType.START_OPERATION
      operation: OperationType
      originStep: WizardStep
      direction?: NavigationDirection
      data?: ImportData
    }

  // Step-aware "Next" — machine handles per-step behavior
  | { type: EventType.NEXT; data?: ImportData; deselectedHashes?: string[] }

  // Step 1: Deselect confirmation
  | { type: EventType.CONFIRM_DESELECT }
  | { type: EventType.CANCEL_DESELECT }

  // Step 3: Confirm matches
  | { type: EventType.CONFIRM_MATCHES }
  | { type: EventType.CANCEL_CONFIRM }

  // Step 4 deal validation
  | { type: EventType.VALIDATE_DEALS }
  | { type: EventType.CANCEL_DEAL_VALIDATION }
  | { type: EventType.DEAL_PRODUCT_SELECTED; productName: string }
  | {
      type: EventType.DEALS_VALIDATION_FIXED
      changes: Array<{ original: string; mapped?: string }>
      columnName: 'deal_product' | 'deal_account_owner' | 'deal_sales_stage'
    }

  // Error recovery
  | { type: EventType.RETRY }

  // Internal events from callback actors (not in EventType enum)
  | { type: 'POLL_COMPLETE' }
  | { type: 'POLL_ERROR'; error: string }

/**
 * Check if an operation requires backend API execution
 */
export function operationNeedsExecution(operation: Operation | null): boolean {
  if (!operation) return false
  return operation.type !== OperationType.NAVIGATE
}

/**
 * Get human-readable message for an operation
 */
export function getOperationMessage(operation: Operation | null): string {
  if (!operation) return 'Processing...'

  switch (operation.type) {
    case OperationType.NAVIGATE:
      return 'Saving changes...'
    case OperationType.SET_COMPANIES:
      return 'Setting up companies...'
    case OperationType.SET_TAGS:
      return 'Importing tags...'
    case OperationType.SET_DEALS:
      return 'Importing deals...'
    case OperationType.SET_CONTACTS:
      return 'Importing contacts...'
    case OperationType.SET_NOTES:
      return 'Importing company notes...'
    default:
      return 'Processing...'
  }
}

/**
 * Lightweight counts for computing available steps.
 * Used by the machine - does NOT compute full arrays (that's the ViewModel's job).
 */
export interface StepAvailabilityCounts {
  areAllRowsSet: boolean
  prematchedCount: number
  pendingReviewCount: number
}

/**
 * Compute minimal counts needed for step availability.
 * This is a lightweight version that only computes what the machine needs.
 * Full company categorization is done in ImporterViewModel.
 */
export function computeStepAvailabilityCounts(
  importData: ImportData | null
): StepAvailabilityCounts {
  if (!importData) {
    return {
      areAllRowsSet: false,
      prematchedCount: 0,
      pendingReviewCount: 0,
    }
  }

  const seenCompanyHashes = new Set<string>()
  let prematchedCount = 0
  let pendingReviewCount = 0
  let readyRowCount = 0
  let setRowCount = 0

  for (const row of importData.rows) {
    const hash = row[InternalColumns.COMPANY_HASH]
    const isReady = row[InternalColumns.READY]
    const isSet =
      row[InternalColumns.SET_CHAIN_PROXY_ID] !== -1 ||
      row[InternalColumns.IS_CHAIN_MERGED]

    if (isReady) readyRowCount++
    if (isSet) setRowCount++

    // Only count each company once (by hash)
    if (!seenCompanyHashes.has(hash)) {
      seenCompanyHashes.add(hash)

      if (isReady) {
        prematchedCount++
      } else {
        pendingReviewCount++
      }
    }
  }

  const areAllRowsSet = setRowCount === readyRowCount && setRowCount > 0

  return {
    areAllRowsSet,
    prematchedCount,
    pendingReviewCount,
  }
}

/**
 * Compute available steps based on step availability counts.
 */
export function computeAvailableSteps(
  counts: StepAvailabilityCounts,
  skipMatching: boolean
): WizardStep[] {
  // If all rows are already set, only step 4 is available
  if (counts.areAllRowsSet) {
    return [WizardStep.STEP4_IMPORT]
  }

  const steps: WizardStep[] = []

  // Step 1: Only if there are pre-matched companies to confirm
  if (counts.prematchedCount > 0 && !skipMatching) {
    steps.push(WizardStep.STEP1_CONFIRM)
  }

  // Step 2: Only if there are pending companies to review
  if (counts.pendingReviewCount > 0 && !skipMatching) {
    steps.push(WizardStep.STEP2_REVIEW)
  }

  // Step 3 & 4: Always available
  steps.push(WizardStep.STEP3_DECIDE)
  steps.push(WizardStep.STEP4_IMPORT)

  return steps
}

/**
 * Compute target step based on operation and available steps
 */
export function computeTargetStep(
  operation: Operation | null,
  availableSteps: WizardStep[]
): WizardStep | null {
  if (!operation) return null
  if (availableSteps.length === 0) return null

  const { type, direction, originStep } = operation
  const originIndex = STEP_ORDER.indexOf(originStep)

  // For process operations (SET_*), return to origin step
  if (type !== OperationType.NAVIGATE) {
    return availableSteps.includes(originStep)
      ? originStep
      : (availableSteps[0] ?? WizardStep.STEP4_IMPORT)
  }

  // NAVIGATE operations use direction
  if (direction === 'stay') {
    return availableSteps.includes(originStep)
      ? originStep
      : (availableSteps[0] ?? WizardStep.STEP4_IMPORT)
  }

  if (direction === 'forward') {
    // Find first available step AFTER origin in wizard order
    for (let i = originIndex + 1; i < STEP_ORDER.length; i++) {
      if (availableSteps.includes(STEP_ORDER[i])) {
        return STEP_ORDER[i]
      }
    }
    // No step after origin, stay at last available
    return availableSteps[availableSteps.length - 1]
  }

  if (direction === 'back') {
    // Find first available step BEFORE origin in wizard order
    for (let i = originIndex - 1; i >= 0; i--) {
      if (availableSteps.includes(STEP_ORDER[i])) {
        return STEP_ORDER[i]
      }
    }
    // No step before origin, stay at first available
    return availableSteps[0]
  }

  return availableSteps[0]
}
